import type { Traducao } from '../tipos';

/**
 * Textos de `apreciacoes`: lista, criação, visualização, editor da apreciação
 * (V1/V2), edição de parte e as mensagens dos hooks de salvar/lock/partes.
 *
 * Convenções deste ramo:
 * - `status.badge` / `status.opcao` são indexados pelo próprio valor de enum da
 *   API (`em_revisao`, `a_tramitar`…). O valor nunca é traduzido; só o rótulo.
 *   São dois conjuntos porque a tabela e o `<select>` sempre tiveram
 *   capitalização diferente em português e a regra 6 manda preservar.
 * - Textos com `{{acao}}` no meio (galeria de fotos, dropzone da IA) são
 *   partidos no marcador em tempo de render para embutir um `<strong>`/botão
 *   sem quebrar a sentença em duas chaves.
 */
export const apreciacoes: Traducao = {
  pt: {
    erroDesconhecido: 'Erro desconhecido',

    aviso: {
      atencao: 'Atenção:',
    },

    status: {
      badge: {
        rascunho: 'Rascunho',
        em_revisao: 'Em revisão',
        aprovado_internamente: 'Aprovado internamente',
        aprovado: 'Aprovado',
        a_tramitar: 'A tramitar',
        tramitado: 'Tramitado',
        tramitado_apos_revisao: 'Tramitado após revisão',
        arquivado: 'Arquivado',
      },
      opcao: {
        rascunho: 'Rascunho',
        em_revisao: 'Em Revisão',
        aprovado_internamente: 'Aprovado Internamente',
        aprovado: 'Aprovado',
        a_tramitar: 'A Tramitar',
        tramitado: 'Tramitado',
        tramitado_apos_revisao: 'Tramitado após revisão',
        arquivado: 'Arquivado',
      },
    },

    lista: {
      titulo: 'Apreciações',
      carregando: 'Carregando apreciações...',
      novaApreciacao: 'Nova apreciação',

      resumo: {
        contagemUma: '{{total}} apreciação',
        contagemVarias: '{{total}} apreciações',
        comAguardando: '{{contagem}} · {{aguardando}} aguardando ação',
      },

      criticidade: {
        critica: 'Crítica',
        alta: 'Alta',
        baixa: 'Baixa',
        na: 'N/A',
      },

      tipo: {
        novo: 'Novo',
        revisao: 'Revisão',
      },

      menu: {
        tituloTabelaSelecionadas: 'Gerar tabela (Área, Tag, N° APR, Emissão) das {{total}} selecionadas',
        tituloTabelaTodas: 'Gerar tabela (Área, Tag, N° APR, Emissão) de todas as apreciações',
        gerandoTabela: 'Gerando tabela...',
        gerarTabela: 'Gerar tabela',
        gerarTabelaContagem: 'Gerar tabela ({{total}})',
        exportando: 'Exportando...',
        exportarExcel: 'Exportar Excel',
      },

      filtro: {
        buscaPlaceholder: 'Buscar por título, número, emitente ou Sismana...',
        ariaStatus: 'Filtrar por status',
        statusTodos: 'Status: todos',
        ariaGrd: 'Filtrar por GRD',
        grdTodas: 'GRD: todas',
        semGrd: 'Sem GRD',
      },

      selecao: {
        uma: '{{total}} selecionada',
        varias: '{{total}} selecionadas',
        guiaRemessa: 'Guia de remessa',
        analisarVerificacoes: 'Analisar verificações',
        gerarTabela: 'Gerar tabela',
        limpar: 'Limpar seleção',
      },

      tabela: {
        selecionarTodas: 'Selecionar todas',
        selecionarApreciacao: 'Selecionar apreciação {{numero}}',
        colApreciacao: 'Apreciação',
        colEmitente: 'Emitente',
        colCriticidade: 'Criticidade',
        colGrd: 'GRD',
      },

      vazio: {
        semDados: 'Nenhuma apreciação encontrada. Clique em "Nova Apreciação" para começar.',
        erro: 'A lista não pôde ser carregada — isso não significa que as apreciações foram perdidas.',
      },

      lock: {
        titulo: 'Editando: {{usuario}}\nExpira em: {{expira}}',
        usuarioPadrao: 'Usuário',
        editando: 'Editando',
        semExpiracao: 'N/A',
      },

      acao: {
        baixarPdf: 'Baixar PDF',
        baixarWord: 'Baixar Word',
      },

      grd: {
        tituloBadge: 'Destinatário: {{destinatario}}\nEmitida em: {{data}}',
        removerDesta: 'Remover {{numero}} desta guia',
      },

      erro: {
        carregar: 'Não foi possível carregar as apreciações.',
        semServidor: 'Não foi possível falar com o servidor. A lista abaixo pode estar desatualizada.',
        offline: 'Sem conexão. A lista abaixo é a última carregada.',
      },

      excel: {
        sucesso: '{{total}} apreciações exportadas',
        erroExportar: 'Erro ao exportar apreciações',
        erroArquivo: 'Erro ao gerar arquivo Excel',
      },

      tabelaControle: {
        nenhuma: 'Nenhuma apreciação para gerar a tabela.',
        sucesso: '{{total}} apreciação(ões) na tabela',
        erro: 'Erro ao gerar tabela',
      },

      word: {
        sucesso: 'Word exportado com sucesso',
        erro: 'Erro ao exportar Word',
      },

      pdf: {
        sucesso: 'PDF exportado com sucesso',
        erro: 'Erro ao exportar PDF',
      },

      guia: {
        selecionePeloMenosUma: 'Selecione pelo menos uma apreciação para gerar a guia.',
        confirmarRemover: 'Remover a apreciação {{numero}} da guia {{guia}}?',
        removida: 'Apreciação {{numero}} removida da {{guia}}.',
        erroRemover: 'Erro ao remover a apreciação da guia.',
        erroSelecioneGrd: 'Selecione a GRD existente.',
        erroDestinatario: 'Informe o destinatário.',
        erroGerar: 'Erro ao gerar guia de remessa.',
        adicionadas: 'Apreciações adicionadas à guia {{numero}}. PDF atualizado baixado.',
        gerada: 'Guia {{numero}} gerada com sucesso.',
      },

      modalGuia: {
        titulo: 'Gerar guia de remessa',
        subtitulo: '{{total}} apreciação(ões) selecionada(s). O PDF será baixado automaticamente.',
        grdReferente: 'GRD referente',
        criarNova: 'Criar nova GRD',
        usarExistente: 'Usar GRD existente',
        grdExistente: 'GRD existente *',
        carregandoGrds: 'Carregando GRDs…',
        semGrds: 'Nenhuma GRD emitida neste projeto',
        selecioneGrd: 'Selecione a GRD…',
        itemUm: '{{total}} item',
        itemVarios: '{{total}} itens',
        opcaoGrd: '{{numero}} — {{destinatario}} ({{itens}}, {{data}})',
        avisoExistente: 'As apreciações selecionadas serão adicionadas à guia e o PDF atualizado será baixado.',
        destinatario: 'Destinatário *',
        destinatarioPlaceholder: 'Ex.: Área de Engenharia / João Silva',
        observacoesPlaceholder: 'Instruções, local de entrega, prazo…',
        gerando: 'Gerando…',
        adicionarGerarPdf: 'Adicionar e gerar PDF',
        gerarPdf: 'Gerar PDF',
      },

      modalRemover: {
        titulo: 'Confirmar exclusão',
        aviso: 'Esta ação não pode ser desfeita. Verifique os dados e digite o código praxis para confirmar:',
        rotuloNome: 'Nome:',
        rotuloCodigo: 'Código:',
        codigoIncorreto: 'Código incorreto. Tente novamente.',
        erroDetalhe: 'Erro ao remover apreciação: {{detalhe}}',
        erro: 'Erro ao remover apreciação.',
      },
    },

    criar: {
      titulo: 'Nova Apreciação',
      subtitulo: 'Crie uma nova apreciação de risco seguindo a estrutura NR-12',
      avisoAtivoEmUso: 'Este ativo está sendo usado por outro usuário para criar uma apreciação. Escolha outro ativo ou aguarde.',
      ativoBloqueado: 'Este ativo está sendo usado por outro usuário para criar uma apreciação. Aguarde ou escolha outro ativo.',
      selecioneProjeto: 'Selecione um projeto primeiro',
      usarTemplate: 'Usar Template (opcional)',
      selecioneTemplate: 'Selecione um template...',
      carregandoTemplate: 'Carregando template...',
      templateSelecionado: 'Template selecionado. Os riscos serão importados ao salvar.',
      erroCarregarTemplate: 'Erro ao carregar template',
      erroCriar: 'Erro ao criar apreciação',
      erroCriarDetalhe: 'Erro ao criar apreciação:\n\n{{detalhe}}',
      erroSalvar: 'Erro ao salvar apreciação. Tente novamente.',
      salvarRascunho: 'Salvar Rascunho',
      criarApreciacao: 'Criar Apreciação',
    },

    ver: {
      carregando: 'Carregando apreciação...',
      naoEncontrada: 'Apreciação não encontrada',
      voltarLista: 'Voltar para lista',
      tituloFallback: 'Apreciação #{{numero}}',
      numeroRevisao: '{{numero}} - Revisão {{revisao}}',
      erroCarregar: 'Erro ao carregar apreciação',
      erroCarregarDetalhe: 'Erro ao carregar apreciação: {{detalhe}}',
      erroAlterarStatus: 'Erro ao alterar status',
      criarTemplate: 'Criar Template',
      exportarPdf: 'Exportar PDF',
      compartilhar: 'Compartilhar',
      erroCompartilhar: 'Erro ao compartilhar',
      confirmarRevogar: 'Revogar compartilhamento?',

      compartilhamento: {
        titulo: 'Compartilhamento com Cliente',
        anotacoes: '{{total}} anotacoes',
        anotacoesEncontradas: '{{total}} anotacoes encontradas. Funcionalidade de visualizacao em breve.',
        verAnotacoes: 'Ver Anotacoes',
        revogar: 'Revogar',
      },

      modalCompartilhar: {
        titulo: 'Compartilhar com Cliente',
        selecioneEmpresa: 'Selecione uma empresa...',
      },

      modalTemplate: {
        titulo: 'Criar Template a partir desta Apreciação',
        nome: 'Nome do Template *',
        nomePlaceholder: 'Ex: Template - Torno CNC',
        descricaoPlaceholder: 'Descrição do template (opcional)',
        nomeObrigatorio: 'Digite um nome para o template',
        criado: 'Template criado com sucesso!',
        erroCriar: 'Erro ao criar template',
        erroCriarDetalhe: 'Erro ao criar template: {{detalhe}}',
      },
    },

    editar: {
      tituloFallback: 'Apreciação {{numero}}',
      verificandoBloqueio: 'Verificando bloqueio...',
      carregandoApreciacao: 'Carregando apreciação...',
      carregandoDados: 'Carregando dados...',

      bloqueio: {
        titulo: 'Apreciação Bloqueada',
        verApreciacao: 'Ver Apreciação',
        voltarLista: 'Voltar para Lista',
      },

      aviso: {
        bloqueada: 'Esta apreciação está sendo editada por outro usuário.',
        bloqueadaLeitura: 'Esta apreciação está sendo editada por outro usuário. Você pode visualizar, mas não pode fazer alterações.',
      },

      tab: {
        edicao: 'Edição',
        preview: 'Visualização (LaTeX)',
        dadosMaquina: 'Dados da Máquina',
        partesMaquina: 'Partes da Máquina',
      },

      templates: {
        titulo: 'Templates Multipartes',
        criarCompleto: 'Criar Template Completo',
        adicionarCompleto: 'Adicionar Template Completo',
      },

      painel: {
        preview: 'Pré-visualização',
        fotosCampo: 'Fotos do campo ({{total}})',
      },

      galeria: {
        dica: 'Fotos capturadas em campo para esta máquina. Use {{acao}} e cole (Ctrl+V) num slot de imagem da parte — ou, ao editar uma parte, clique na foto para adicioná-la direto.',
      },

      encaixe: {
        tituloBotao: 'A IA descreve as fotos, escolhe até 4 por parte e sugere os textos',
        analisando: 'Analisando fotos com IA...',
        reprocessar: 'Reprocessar fotos (IA)',
        encaixar: 'Encaixar fotos nas partes (IA)',
        tituloUltima: 'Reabre a última análise salva, sem reprocessar',
        verUltima: 'Ver última análise',
        falha: 'Falha ao encaixar fotos',
        erro: 'Erro ao encaixar fotos',
        semRiscoParte: 'Nenhum risco encontrado para a parte "{{parte}}".',
        erroAplicar: 'Erro ao aplicar sugestão',
        modalTitulo: 'Sugestões de encaixe de fotos',
        modalSubtitulo: '{{total}} foto(s) analisada(s). Revise e aplique por parte.',
        geradaEm: ' · Gerada em {{data}}',
        aplicarParte: 'Aplicar a esta parte',
        semFotos: 'Nenhuma foto referente encontrada.',
        condicaoAtual: 'Condição atual:',
        perigos: 'Perigos:',
        consequencias: 'Consequências:',
        eventoPerigoso: 'Evento perigoso:',
      },

      json: {
        titulo: 'Importar Parte por JSON',
        subtitulo: 'Cole um JSON com riscos. Você pode informar as imagens da parte para aplicar em todos os riscos.',
        nomeParte: 'Nome da parte (opcional)',
        nomePartePlaceholder: 'Ex.: Alimentador Principal',
        imagemParte: 'Imagem parte {{indice}}',
        selecionado: 'Selecionado: {{arquivo}}',
        arraste: 'Arraste uma imagem aqui ou selecione arquivo',
        rotuloJson: 'JSON',
        exemploPlaceholder: `Exemplo:
{
  "parte": "Proteção Frontal",
  "riscos": [
    {
      "eventoPerigoso": "Contato com partes móveis",
      "consequencias": "Lesão em membros",
      "tipoPerigo": "mecanico"
    }
  ]
}`,
        importando: 'Importando...',
        ok: 'OK',
        semId: 'ID da apreciação não encontrado.',
        semJson: 'Cole um JSON para importar.',
        semRiscos: 'JSON sem riscos para importar.',
        erroImagem: 'Erro ao enviar imagem da parte {{indice}}',
        erroRisco: 'Falha ao criar risco número {{numero}}',
        concluida: 'Importação concluída: {{total}} risco(s) criado(s) na parte "{{parte}}".',
        erroImportar: 'Erro ao importar JSON: {{detalhe}}',
        jsonInvalido: 'JSON inválido ou erro ao salvar riscos.',
      },

      v2: {
        badge: 'EDIÇÃO V2 (EXPERIMENTAL — COM RECURSOS DE IA)',
        copiarTitulo: 'Copiar de outra apreciação',
        copiarSubtitulo: 'Sobrescreve cabeçalho, identificação, revisão, executantes e dados da máquina. Não copia partes.',
        escolherApreciacao: 'Escolher apreciação…',
        erroBaixarImagens: 'Erro ao baixar imagens: {{detalhe}}',
        erroDetalharIA: 'Erro ao detalhar com IA: {{detalhe}}',
      },

      copiar: {
        titulo: 'Copiar de outra apreciação',
        subtitulo: 'Todos os campos de cabeçalho, identificação documental, revisão, executantes e máquina serão sobrescritos. As partes (riscos) NÃO são copiadas.',
        buscaPlaceholder: 'Buscar por título, número ou máquina…',
        carregando: 'Carregando apreciações…',
        vazio: 'Nenhuma apreciação encontrada.',
        semTitulo: '(sem título)',
        maquina: 'Máquina: {{nome}}',
        copiando: 'Copiando…',
        confirmar: 'Copiar cabeçalho',
        erroLista: 'Não foi possível carregar a lista',
        erroCarregar: 'Erro ao carregar lista',
        falha: 'Falha ao copiar cabeçalho',
        erro: 'Erro ao copiar cabeçalho',
      },

      ia: {
        titulo: 'Gerar partes com IA (experimental)',
        subtitulo: 'Arraste imagens da máquina. A IA: (1) descreve cada foto com Gemini 3.1 Flash, (2) agrupa em partes com Gemini 3.1 Flash, (3) detalha cada parte com Gemini 3.1 Flash. As partes são acrescentadas às existentes.',
        dropzone: 'Arraste imagens aqui, cole do clipboard ou {{acao}}.',
        selecioneArquivos: 'selecione arquivos',
        prontas: '{{total}} imagem(ns) pronta(s) para envio',
        processando: 'Processando…',
        gerar: 'Gerar partes com IA',
        passoDescrever: 'Descrevendo imagens (Gemini Flash)',
        passoAgrupar: 'Agrupando em partes (Gemini Flash)',
        passoDetalhar: 'Detalhando cada parte (Gemini Flash)',
        progressoImagens: '{{feitas}}/{{total}} imagens descritas',
        progressoPartes: '{{feitas}}/{{total}} partes detalhadas',
        falhaLote: 'Falha ao descrever lote {{lote}}',
        falhaAgrupar: 'Falha ao agrupar partes',
        resumo: '{{criadas}} parte(s) criadas ({{grupos}} grupo(s) detectados).',
        resumoComErros: '{{criadas}} parte(s) criadas ({{grupos}} grupo(s) detectados), {{erros}} erro(s).',
        erroGerar: 'Erro ao gerar partes com IA',
      },
    },

    parte: {
      carregando: 'Carregando dados da parte...',
      semNome: 'Nome da parte não encontrado',
      voltar: 'Voltar para edição da apreciação',
      erroCarregar: 'Erro ao carregar dados da parte',
      salvaSucesso: 'Parte salva com sucesso!',
      erroSalvar: 'Erro ao salvar parte',
      erroImagem: 'Erro ao processar a imagem. Tente converter para JPG ou PNG antes de fazer upload.',

      imagensTitulo: 'Imagens da Parte (até 4 imagens)',
      imagemIndice: 'Imagem {{indice}}',
      altImagem: 'Parte {{indice}}',

      cicloVidaTitulo: 'Ciclo de Vida',
      novoCicloPlaceholder: 'Adicionar novo ciclo de vida',
      cicloVida: {
        ajuste: 'Ajuste',
        limpeza: 'Limpeza',
        manutencao: 'Manutenção',
        operacaoNormal: 'Operação Normal',
      },

      perigosMecanicos: 'Perigos Mecânicos',
      eventoPerigoso: 'Evento Perigoso',
      eventoPerigosoPlaceholder: 'Ex: Lesão ocasionada pelo giro da placa',
      statusConformidade: 'Status de Conformidade',
      conforme: 'Conforme',
      naoConforme: 'Não Conforme',
      perigosEncontrados: 'Perigos Encontrados',
      adicionarPerigo: 'Adicionar perigo',
      potenciaisConsequencias: 'Potenciais Consequências',
      adicionarConsequencia: 'Adicionar consequência',

      adequacoesTecnicas: 'Adequações Técnicas',
      descricaoCondicaoAtual: 'Descrição da Condição Atual',
      medidasControle: 'Medidas de Controle',
      exigenciasNormativas: 'Exigências Normativas',
      adequacoesAdministrativas: 'Adequações Administrativas',
      digitarEnter: 'Digitar e pressionar Enter ou +',

      hrnAntes: 'HRN Antes',
      hrnDepois: 'HRN Depois',
      colRisco: 'Risco',
      colHrn: 'HRN',
      riscoIndice: 'Risco {{indice}}',

      pronta: 'Parte está pronta',
      salvarAlteracoes: 'Salvar Alterações',
    },

    salvar: {
      bloqueada: 'Esta apreciação está sendo editada por outro usuário. Não é possível salvar alterações.',
      sucesso: 'Apreciação salva com sucesso!',
      erroDetalhe: 'Erro ao salvar apreciação: {{detalhe}}',
      semParte: 'sem parte',
      erroMinusculo: 'erro desconhecido',
      itemNaoSalvo: '{{parte}} (nº {{numero}}): {{erro}}',
      parcialUma: 'A apreciação foi salva, mas {{total}} parte não foi gravada:\n\n{{partes}}\n\nConfira a lista de partes e salve novamente as que faltaram.',
      parcialVarias: 'A apreciação foi salva, mas {{total}} partes não foram gravadas:\n\n{{partes}}\n\nConfira a lista de partes e salve novamente as que faltaram.',

      lock: {
        editadaPorOutro: 'Esta apreciação está sendo editada por outro usuário.',
        bloqueadoPor: 'Bloqueado por: {{usuario}}',
        expiraEm: 'Expira em: {{data}}',
        erroVerificar: 'Erro ao verificar bloqueio. Tente novamente.',
      },

      partes: {
        semId: 'ID da apreciação não encontrado',
        erroRemoverRisco: 'Erro ao remover risco {{numero}}: {{detalhe}}',
        erroRemoverAlguns: 'Alguns riscos não puderam ser removidos: {{detalhe}}',
        erroDeletar: 'Erro ao deletar parte: {{detalhe}}',
        erroSalvarRisco: 'Erro ao salvar risco {{numero}}: {{detalhe}}',
        erroAtualizarRisco: 'Erro ao atualizar risco',
        erroAdicionarRisco: 'Erro ao adicionar risco',
        duplicada: 'Parte "{{parte}}" duplicada com sucesso! {{total}} risco(s) criado(s).',
        erroDuplicar: 'Erro ao duplicar parte: {{detalhe}}',
        erroReordenar: 'Erro ao reordenar: {{detalhe}}',
      },
    },
  },

  en: {
    erroDesconhecido: 'Unknown error',

    aviso: {
      atencao: 'Warning:',
    },

    status: {
      badge: {
        rascunho: 'Draft',
        em_revisao: 'Under review',
        aprovado_internamente: 'Internally approved',
        aprovado: 'Approved',
        a_tramitar: 'To submit',
        tramitado: 'Submitted',
        tramitado_apos_revisao: 'Submitted after review',
        arquivado: 'Archived',
      },
      opcao: {
        rascunho: 'Draft',
        em_revisao: 'Under Review',
        aprovado_internamente: 'Internally Approved',
        aprovado: 'Approved',
        a_tramitar: 'To Submit',
        tramitado: 'Submitted',
        tramitado_apos_revisao: 'Submitted after review',
        arquivado: 'Archived',
      },
    },

    lista: {
      titulo: 'Risk Assessments',
      carregando: 'Loading risk assessments...',
      novaApreciacao: 'New assessment',

      resumo: {
        contagemUma: '{{total}} assessment',
        contagemVarias: '{{total}} assessments',
        comAguardando: '{{contagem}} · {{aguardando}} awaiting action',
      },

      criticidade: {
        critica: 'Critical',
        alta: 'High',
        baixa: 'Low',
        na: 'N/A',
      },

      tipo: {
        novo: 'New',
        revisao: 'Revision',
      },

      menu: {
        tituloTabelaSelecionadas: 'Generate table (Area, Tag, APR No., Issue date) for the {{total}} selected',
        tituloTabelaTodas: 'Generate table (Area, Tag, APR No., Issue date) for every assessment',
        gerandoTabela: 'Generating table...',
        gerarTabela: 'Generate table',
        gerarTabelaContagem: 'Generate table ({{total}})',
        exportando: 'Exporting...',
        exportarExcel: 'Export to Excel',
      },

      filtro: {
        buscaPlaceholder: 'Search by title, number, issuer or Sismana...',
        ariaStatus: 'Filter by status',
        statusTodos: 'Status: all',
        ariaGrd: 'Filter by delivery note',
        grdTodas: 'GRD: all',
        semGrd: 'No GRD',
      },

      selecao: {
        uma: '{{total}} selected',
        varias: '{{total}} selected',
        guiaRemessa: 'Delivery note',
        analisarVerificacoes: 'Review checks',
        gerarTabela: 'Generate table',
        limpar: 'Clear selection',
      },

      tabela: {
        selecionarTodas: 'Select all',
        selecionarApreciacao: 'Select assessment {{numero}}',
        colApreciacao: 'Assessment',
        colEmitente: 'Issuer',
        colCriticidade: 'Criticality',
        colGrd: 'GRD',
      },

      vazio: {
        semDados: 'No risk assessment found. Click "New assessment" to get started.',
        erro: 'The list could not be loaded — this does not mean the assessments were lost.',
      },

      lock: {
        titulo: 'Editing: {{usuario}}\nExpires at: {{expira}}',
        usuarioPadrao: 'User',
        editando: 'Editing',
        semExpiracao: 'N/A',
      },

      acao: {
        baixarPdf: 'Download PDF',
        baixarWord: 'Download Word',
      },

      grd: {
        tituloBadge: 'Recipient: {{destinatario}}\nIssued on: {{data}}',
        removerDesta: 'Remove {{numero}} from this delivery note',
      },

      erro: {
        carregar: 'Could not load the risk assessments.',
        semServidor: 'Could not reach the server. The list below may be out of date.',
        offline: 'No connection. The list below is the last one loaded.',
      },

      excel: {
        sucesso: '{{total}} assessments exported',
        erroExportar: 'Could not export the assessments',
        erroArquivo: 'Could not generate the Excel file',
      },

      tabelaControle: {
        nenhuma: 'No assessment to build the table from.',
        sucesso: '{{total}} assessment(s) in the table',
        erro: 'Could not generate the table',
      },

      word: {
        sucesso: 'Word exported successfully',
        erro: 'Could not export to Word',
      },

      pdf: {
        sucesso: 'PDF exported successfully',
        erro: 'Could not export to PDF',
      },

      guia: {
        selecionePeloMenosUma: 'Select at least one assessment to generate the delivery note.',
        confirmarRemover: 'Remove assessment {{numero}} from delivery note {{guia}}?',
        removida: 'Assessment {{numero}} removed from {{guia}}.',
        erroRemover: 'Could not remove the assessment from the delivery note.',
        erroSelecioneGrd: 'Select the existing GRD.',
        erroDestinatario: 'Enter the recipient.',
        erroGerar: 'Could not generate the delivery note.',
        adicionadas: 'Assessments added to delivery note {{numero}}. Updated PDF downloaded.',
        gerada: 'Delivery note {{numero}} generated successfully.',
      },

      modalGuia: {
        titulo: 'Generate delivery note',
        subtitulo: '{{total}} assessment(s) selected. The PDF will be downloaded automatically.',
        grdReferente: 'Related GRD',
        criarNova: 'Create new GRD',
        usarExistente: 'Use existing GRD',
        grdExistente: 'Existing GRD *',
        carregandoGrds: 'Loading GRDs…',
        semGrds: 'No GRD issued in this project',
        selecioneGrd: 'Select the GRD…',
        itemUm: '{{total}} item',
        itemVarios: '{{total}} items',
        opcaoGrd: '{{numero}} — {{destinatario}} ({{itens}}, {{data}})',
        avisoExistente: 'The selected assessments will be added to the delivery note and the updated PDF will be downloaded.',
        destinatario: 'Recipient *',
        destinatarioPlaceholder: 'e.g. Engineering department / John Smith',
        observacoesPlaceholder: 'Instructions, delivery location, deadline…',
        gerando: 'Generating…',
        adicionarGerarPdf: 'Add and generate PDF',
        gerarPdf: 'Generate PDF',
      },

      modalRemover: {
        titulo: 'Confirm deletion',
        aviso: 'This action cannot be undone. Check the details and type the praxis code to confirm:',
        rotuloNome: 'Name:',
        rotuloCodigo: 'Code:',
        codigoIncorreto: 'Wrong code. Try again.',
        erroDetalhe: 'Could not delete the assessment: {{detalhe}}',
        erro: 'Could not delete the assessment.',
      },
    },

    criar: {
      titulo: 'New Risk Assessment',
      subtitulo: 'Create a new risk assessment following the NR-12 structure',
      avisoAtivoEmUso: 'This asset is being used by another user to create an assessment. Pick another asset or wait.',
      ativoBloqueado: 'This asset is being used by another user to create an assessment. Wait or pick another asset.',
      selecioneProjeto: 'Select a project first',
      usarTemplate: 'Use template (optional)',
      selecioneTemplate: 'Select a template...',
      carregandoTemplate: 'Loading template...',
      templateSelecionado: 'Template selected. The hazards will be imported on save.',
      erroCarregarTemplate: 'Could not load the template',
      erroCriar: 'Could not create the assessment',
      erroCriarDetalhe: 'Could not create the assessment:\n\n{{detalhe}}',
      erroSalvar: 'Could not save the assessment. Try again.',
      salvarRascunho: 'Save Draft',
      criarApreciacao: 'Create Assessment',
    },

    ver: {
      carregando: 'Loading risk assessment...',
      naoEncontrada: 'Risk assessment not found',
      voltarLista: 'Back to list',
      tituloFallback: 'Assessment #{{numero}}',
      numeroRevisao: '{{numero}} - Revision {{revisao}}',
      erroCarregar: 'Could not load the assessment',
      erroCarregarDetalhe: 'Could not load the assessment: {{detalhe}}',
      erroAlterarStatus: 'Could not change the status',
      criarTemplate: 'Create Template',
      exportarPdf: 'Export PDF',
      compartilhar: 'Share',
      erroCompartilhar: 'Could not share',
      confirmarRevogar: 'Revoke sharing?',

      compartilhamento: {
        titulo: 'Shared with Client',
        anotacoes: '{{total}} notes',
        anotacoesEncontradas: '{{total}} notes found. Viewing is coming soon.',
        verAnotacoes: 'View Notes',
        revogar: 'Revoke',
      },

      modalCompartilhar: {
        titulo: 'Share with Client',
        selecioneEmpresa: 'Select a company...',
      },

      modalTemplate: {
        titulo: 'Create Template from this Assessment',
        nome: 'Template Name *',
        nomePlaceholder: 'e.g. Template - CNC Lathe',
        descricaoPlaceholder: 'Template description (optional)',
        nomeObrigatorio: 'Enter a name for the template',
        criado: 'Template created successfully!',
        erroCriar: 'Could not create the template',
        erroCriarDetalhe: 'Could not create the template: {{detalhe}}',
      },
    },

    editar: {
      tituloFallback: 'Assessment {{numero}}',
      verificandoBloqueio: 'Checking lock...',
      carregandoApreciacao: 'Loading risk assessment...',
      carregandoDados: 'Loading data...',

      bloqueio: {
        titulo: 'Assessment Locked',
        verApreciacao: 'View Assessment',
        voltarLista: 'Back to List',
      },

      aviso: {
        bloqueada: 'This assessment is being edited by another user.',
        bloqueadaLeitura: 'This assessment is being edited by another user. You can view it, but you cannot make changes.',
      },

      tab: {
        edicao: 'Editing',
        preview: 'Preview (LaTeX)',
        dadosMaquina: 'Machine Data',
        partesMaquina: 'Machine Parts',
      },

      templates: {
        titulo: 'Multi-part Templates',
        criarCompleto: 'Create Full Template',
        adicionarCompleto: 'Add Full Template',
      },

      painel: {
        preview: 'Preview',
        fotosCampo: 'Field photos ({{total}})',
      },

      galeria: {
        dica: 'Photos captured in the field for this machine. Use {{acao}} and paste (Ctrl+V) into an image slot of the part — or, while editing a part, click the photo to add it directly.',
      },

      encaixe: {
        tituloBotao: 'The AI describes the photos, picks up to 4 per part and suggests the texts',
        analisando: 'Analysing photos with AI...',
        reprocessar: 'Reprocess photos (AI)',
        encaixar: 'Match photos to parts (AI)',
        tituloUltima: 'Reopens the last saved analysis, without reprocessing',
        verUltima: 'View last analysis',
        falha: 'Could not match the photos',
        erro: 'Error while matching the photos',
        semRiscoParte: 'No hazard found for part "{{parte}}".',
        erroAplicar: 'Could not apply the suggestion',
        modalTitulo: 'Photo matching suggestions',
        modalSubtitulo: '{{total}} photo(s) analysed. Review and apply per part.',
        geradaEm: ' · Generated on {{data}}',
        aplicarParte: 'Apply to this part',
        semFotos: 'No matching photo found.',
        condicaoAtual: 'Current condition:',
        perigos: 'Hazards:',
        consequencias: 'Consequences:',
        eventoPerigoso: 'Hazardous event:',
      },

      json: {
        titulo: 'Import Part from JSON',
        subtitulo: 'Paste a JSON with hazards. You can supply the part images to apply to every hazard.',
        nomeParte: 'Part name (optional)',
        nomePartePlaceholder: 'e.g. Main feeder',
        imagemParte: 'Part image {{indice}}',
        selecionado: 'Selected: {{arquivo}}',
        arraste: 'Drag an image here or pick a file',
        rotuloJson: 'JSON',
        exemploPlaceholder: `Example:
{
  "parte": "Front guard",
  "riscos": [
    {
      "eventoPerigoso": "Contact with moving parts",
      "consequencias": "Limb injury",
      "tipoPerigo": "mecanico"
    }
  ]
}`,
        importando: 'Importing...',
        ok: 'OK',
        semId: 'Assessment ID not found.',
        semJson: 'Paste a JSON to import.',
        semRiscos: 'JSON has no hazards to import.',
        erroImagem: 'Could not upload image {{indice}} of the part',
        erroRisco: 'Could not create hazard number {{numero}}',
        concluida: 'Import finished: {{total}} hazard(s) created in part "{{parte}}".',
        erroImportar: 'Could not import the JSON: {{detalhe}}',
        jsonInvalido: 'Invalid JSON or error while saving the hazards.',
      },

      v2: {
        badge: 'EDITOR V2 (EXPERIMENTAL — WITH AI FEATURES)',
        copiarTitulo: 'Copy from another assessment',
        copiarSubtitulo: 'Overwrites header, identification, revision, executors and machine data. Parts are not copied.',
        escolherApreciacao: 'Choose assessment…',
        erroBaixarImagens: 'Could not download the images: {{detalhe}}',
        erroDetalharIA: 'Could not detail with AI: {{detalhe}}',
      },

      copiar: {
        titulo: 'Copy from another assessment',
        subtitulo: 'Every header, document identification, revision, executor and machine field will be overwritten. The parts (hazards) are NOT copied.',
        buscaPlaceholder: 'Search by title, number or machine…',
        carregando: 'Loading risk assessments…',
        vazio: 'No risk assessment found.',
        semTitulo: '(untitled)',
        maquina: 'Machine: {{nome}}',
        copiando: 'Copying…',
        confirmar: 'Copy header',
        erroLista: 'Could not load the list',
        erroCarregar: 'Error while loading the list',
        falha: 'Could not copy the header',
        erro: 'Error while copying the header',
      },

      ia: {
        titulo: 'Generate parts with AI (experimental)',
        subtitulo: 'Drag machine images in. The AI: (1) describes each photo with Gemini 3.1 Flash, (2) groups them into parts with Gemini 3.1 Flash, (3) details each part with Gemini 3.1 Flash. The parts are appended to the existing ones.',
        dropzone: 'Drag images here, paste from the clipboard or {{acao}}.',
        selecioneArquivos: 'pick files',
        prontas: '{{total}} image(s) ready to send',
        processando: 'Processing…',
        gerar: 'Generate parts with AI',
        passoDescrever: 'Describing images (Gemini Flash)',
        passoAgrupar: 'Grouping into parts (Gemini Flash)',
        passoDetalhar: 'Detailing each part (Gemini Flash)',
        progressoImagens: '{{feitas}}/{{total}} images described',
        progressoPartes: '{{feitas}}/{{total}} parts detailed',
        falhaLote: 'Could not describe batch {{lote}}',
        falhaAgrupar: 'Could not group the parts',
        resumo: '{{criadas}} part(s) created ({{grupos}} group(s) detected).',
        resumoComErros: '{{criadas}} part(s) created ({{grupos}} group(s) detected), {{erros}} error(s).',
        erroGerar: 'Could not generate the parts with AI',
      },
    },

    parte: {
      carregando: 'Loading part data...',
      semNome: 'Part name not found',
      voltar: 'Back to assessment editing',
      erroCarregar: 'Could not load the part data',
      salvaSucesso: 'Part saved successfully!',
      erroSalvar: 'Could not save the part',
      erroImagem: 'Could not process the image. Try converting it to JPG or PNG before uploading.',

      imagensTitulo: 'Part Images (up to 4 images)',
      imagemIndice: 'Image {{indice}}',
      altImagem: 'Part {{indice}}',

      cicloVidaTitulo: 'Life Cycle',
      novoCicloPlaceholder: 'Add new life cycle',
      cicloVida: {
        ajuste: 'Adjustment',
        limpeza: 'Cleaning',
        manutencao: 'Maintenance',
        operacaoNormal: 'Normal Operation',
      },

      perigosMecanicos: 'Mechanical Hazards',
      eventoPerigoso: 'Hazardous Event',
      eventoPerigosoPlaceholder: 'e.g. Injury caused by the chuck rotation',
      statusConformidade: 'Compliance Status',
      conforme: 'Compliant',
      naoConforme: 'Non-compliant',
      perigosEncontrados: 'Hazards Found',
      adicionarPerigo: 'Add hazard',
      potenciaisConsequencias: 'Potential Consequences',
      adicionarConsequencia: 'Add consequence',

      adequacoesTecnicas: 'Technical Adaptations',
      descricaoCondicaoAtual: 'Description of the Current Condition',
      medidasControle: 'Control Measures',
      exigenciasNormativas: 'Regulatory Requirements',
      adequacoesAdministrativas: 'Administrative Adaptations',
      digitarEnter: 'Type and press Enter or +',

      hrnAntes: 'HRN Before',
      hrnDepois: 'HRN After',
      colRisco: 'Risk',
      colHrn: 'HRN',
      riscoIndice: 'Risk {{indice}}',

      pronta: 'Part is ready',
      salvarAlteracoes: 'Save Changes',
    },

    salvar: {
      bloqueada: 'This assessment is being edited by another user. Changes cannot be saved.',
      sucesso: 'Risk assessment saved successfully!',
      erroDetalhe: 'Could not save the assessment: {{detalhe}}',
      semParte: 'no part',
      erroMinusculo: 'unknown error',
      itemNaoSalvo: '{{parte}} (no. {{numero}}): {{erro}}',
      parcialUma: 'The assessment was saved, but {{total}} part was not written:\n\n{{partes}}\n\nCheck the part list and save the missing ones again.',
      parcialVarias: 'The assessment was saved, but {{total}} parts were not written:\n\n{{partes}}\n\nCheck the part list and save the missing ones again.',

      lock: {
        editadaPorOutro: 'This assessment is being edited by another user.',
        bloqueadoPor: 'Locked by: {{usuario}}',
        expiraEm: 'Expires at: {{data}}',
        erroVerificar: 'Could not check the lock. Try again.',
      },

      partes: {
        semId: 'Assessment ID not found',
        erroRemoverRisco: 'Could not remove hazard {{numero}}: {{detalhe}}',
        erroRemoverAlguns: 'Some hazards could not be removed: {{detalhe}}',
        erroDeletar: 'Could not delete the part: {{detalhe}}',
        erroSalvarRisco: 'Could not save hazard {{numero}}: {{detalhe}}',
        erroAtualizarRisco: 'Could not update the hazard',
        erroAdicionarRisco: 'Could not add the hazard',
        duplicada: 'Part "{{parte}}" duplicated successfully! {{total}} hazard(s) created.',
        erroDuplicar: 'Could not duplicate the part: {{detalhe}}',
        erroReordenar: 'Could not reorder: {{detalhe}}',
      },
    },
  },
};
